import axios from 'axios';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { getAuthConfig } from '../config/authfusion';
import getConfig from '../config/environments';

// Decode a JWT payload without verifying it (the gateway verifies tokens; the
// portal only needs exp and display claims). Returns null when unreadable.
export const decodeJwtClaims = (token) => {
  try {
    const part = String(token).split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      Array.from(window.atob(padded), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

class AuthFusionService {
  constructor() {
    this.userManager = null;
    this.user = null;
    this._refreshPromise = null;
    this.gatewayURL = getConfig().api.gatewayURL;
    this.initUserManager();
  }

  initUserManager() {
    const config = getAuthConfig();

    this.userManager = new UserManager({
      authority: config.authority,
      client_id: config.client_id,
      redirect_uri: config.redirect_uri,
      post_logout_redirect_uri: config.post_logout_redirect_uri,
      scope: config.scope,
      response_type: config.response_type,
      loadUserInfo: config.loadUserInfo,
      automaticSilentRenew: config.automaticSilentRenew,
      silent_redirect_uri: config.silent_redirect_uri,
      monitorSession: config.monitorSession,
      checkSessionInterval: config.checkSessionInterval,
      metadata: config.metadata,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      stateStore: new WebStorageStateStore({ store: window.localStorage }),
    });

    this.userManager.events.addUserLoaded((loadedUser) => {
      this.user = loadedUser;
      window.dispatchEvent(new CustomEvent('auth:userLoaded', { detail: loadedUser }));
    });

    this.userManager.events.addUserUnloaded(() => {
      this.user = null;
      window.dispatchEvent(new CustomEvent('auth:userUnloaded'));
    });

    // Refresh shortly before the access token expires (oidc-client fires this
    // ~60s ahead), using the stored refresh token, so an active operator is
    // never bounced to sign in again after an hour.
    this.userManager.events.addAccessTokenExpiring(() => {
      this.startSilentRenew().catch(() => {});
    });

    this.userManager.events.addAccessTokenExpired(() => {
      this.user = null;
      window.dispatchEvent(new CustomEvent('auth:tokenExpired'));
    });

    this.userManager.events.addSilentRenewError(() => {
      this.getUser().then((loadedUser) => {
        if (!loadedUser || loadedUser.expired) {
          this.user = null;
          window.dispatchEvent(new CustomEvent('auth:silentRenewFailed'));
        }
      });
    });

    this.userManager.events.addUserSignedOut(() => {
      this.user = null;
      window.dispatchEvent(new CustomEvent('auth:userSignedOut'));
    });
  }

  async signinRedirect() {
    await this.userManager.signinRedirect();
  }

  async signinWithCredentials(email, password) {
    const response = await axios.post(`${this.gatewayURL}/v1/auth/portal/login`, {
      email,
      password,
    });
    return this.storeSession(response.data);
  }

  async storeSession(session) {
    const expiresAt =
      session.profile?.exp ??
      Math.floor(Date.now() / 1000) + session.expiresIn;
    const loadedUser = new User({
      access_token: session.accessToken,
      refresh_token: session.refreshToken,
      token_type: 'Bearer',
      scope: 'openid profile email offline_access',
      profile: session.profile,
      expires_at: expiresAt,
    });

    await this.userManager.storeUser(loadedUser);
    await this.armTokenTimers(loadedUser);
    this.user = loadedUser;
    window.dispatchEvent(
      new CustomEvent('auth:userLoaded', { detail: loadedUser }),
    );
    return loadedUser;
  }

  async signinRedirectCallback() {
    try {
      const loadedUser = await this.userManager.signinRedirectCallback();
      this.user = loadedUser;
      return loadedUser;
    } catch (error) {
      if (error.message && error.message.includes('No matching state')) {
        const existingUser = await this.getUser();
        if (existingUser && !existingUser.expired) {
          return existingUser;
        }
      }
      throw error;
    }
  }

  async signoutRedirect() {
    await this.userManager.signoutRedirect();
  }

  async signoutPortal() {
    const refreshToken = this.user?.refresh_token;

    try {
      if (refreshToken) {
        await axios.post(`${this.gatewayURL}/v1/auth/portal/logout`, {
          refreshToken,
        });
      }
    } finally {
      await this.removeUser();
      window.dispatchEvent(new CustomEvent('auth:userUnloaded'));
    }
  }

  async signoutRedirectCallback() {
    await this.userManager.signoutRedirectCallback();
  }

  async getUser() {
    try {
      const loadedUser = await this.userManager.getUser();
      this.user = loadedUser;
      return loadedUser;
    } catch {
      return null;
    }
  }

  async removeUser() {
    await this.userManager.removeUser();
    this.user = null;
  }

  /**
   * Exchange the stored refresh token for a new access token at the FusionAuth
   * token endpoint (refresh_token grant, public client). This is how portal
   * password sessions stay signed in; they have no hosted-login session for an
   * iframe renew to use. Returns null when there is no refresh token.
   */
  async refreshWithRefreshToken() {
    const stored = this.user?.refresh_token ? this.user : await this.userManager.getUser();
    const refreshToken = stored?.refresh_token;
    if (!refreshToken) return null;

    const config = getAuthConfig();
    const tokenEndpoint = config.metadata?.token_endpoint || `${config.authority}/oauth2/token`;
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: config.client_id,
    });
    const response = await axios.post(tokenEndpoint, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    const data = response.data || {};
    if (!data.access_token) throw new Error('Token refresh returned no access token');

    const claims = decodeJwtClaims(data.access_token) || {};
    const expiresAt =
      claims.exp ?? Math.floor(Date.now() / 1000) + (Number(data.expires_in) || 3600);
    const refreshedUser = new User({
      access_token: data.access_token,
      // FusionAuth may rotate the refresh token; keep the old one if it doesn't.
      refresh_token: data.refresh_token || refreshToken,
      id_token: stored.id_token,
      token_type: 'Bearer',
      scope: stored.scope || 'openid profile email offline_access',
      profile: { ...(stored.profile || {}), ...claims },
      expires_at: expiresAt,
    });
    await this.userManager.storeUser(refreshedUser);
    await this.armTokenTimers(refreshedUser);
    return refreshedUser;
  }

  // storeUser() alone doesn't schedule oidc-client's expiring/expired events;
  // load them for the new token so the proactive refresh fires.
  async armTokenTimers(user) {
    if (typeof this.userManager.events.load === 'function') {
      await this.userManager.events.load(user, false);
    }
  }

  async renewSession() {
    const refreshed = await this.refreshWithRefreshToken();
    if (refreshed) return refreshed;
    // Legacy hosted-login sessions without a refresh token.
    return this.userManager.signinSilent();
  }

  /** Clear an unrecoverable session so the app routes to the embedded /login form. */
  async endExpiredSession() {
    try {
      await this.removeUser();
    } finally {
      window.dispatchEvent(new CustomEvent('auth:userUnloaded'));
    }
  }

  async startSilentRenew() {
    if (this._refreshPromise) {
      return this._refreshPromise;
    }

    this._refreshPromise = this.renewSession()
      .then((loadedUser) => {
        if (loadedUser) {
          this.user = loadedUser;
          window.dispatchEvent(new CustomEvent('auth:tokenRefreshed', { detail: loadedUser }));
        }
        return loadedUser;
      })
      .finally(() => {
        this._refreshPromise = null;
      });

    return this._refreshPromise;
  }

  getAccessToken() {
    return this.user?.access_token;
  }

  getUserId() {
    return this.user?.profile?.sub;
  }

  getUserEmail() {
    return this.user?.profile?.email;
  }

  getUserName() {
    const p = this.user?.profile;
    if (!p) return undefined;
    if (p.name) return p.name;
    if (p.given_name || p.family_name) {
      return [p.given_name, p.family_name].filter(Boolean).join(' ');
    }
    return p.preferred_username || p.email;
  }

  getUserProfile() {
    return this.user?.profile;
  }
}

const authFusionService = new AuthFusionService();

export default authFusionService;
